import React, { useEffect, useContext, useState, useRef } from "react"
import { useFormik } from "formik"
import { navigate } from "gatsby"
import clsx from "clsx"
import Select from "@ecom/ui/components/Select"
import pushToDataLayer from "@ecom/ui/utils/pushToDataLayer"
import {
  pushToDataLayerInvalidField,
  pushToDataLayerLoadedStep,
  pushToDataLayerSendForm,
} from "@ecom/ui/utils/pushToDataLayerUtils"

import getAdditionalData from "@ecom/ui/helpers/getAdditionalData"
import FormHelperText from "@material-ui/core/FormHelperText"
import CircularProgress from "@material-ui/core/CircularProgress"
import Button from "@material-ui/core/Button"
import MUITexteField from "@material-ui/core/TextField"
import {
  DLClickEventExtended,
  DLFormSendEvent,
  DLGAFormEvent,
} from "../../../helpers/WatcherDL/DLEvents"
import { FioField } from "./fields/FioField"
import { BirthField } from "./fields/BirthField"
import { CityField } from "./fields/CityField"
import { AcceptmentField } from "./fields/AcceptmentField"
import { PickupForForm } from "../../PickupOfficeForForm"

import { setIDBValue } from "../../../utils/idbUtils"

import { sendRequest } from "../../../helpers/sendRequest"
import { formatBirthDate } from "../../../helpers/formHelpers"
import { events } from "../../../helpers/events"
import { CalcWithFormContext } from "../../../context"

import { FormSchema } from "./FormSchema"

import * as styles from "./personalForm.module.scss"
import { ChoiceProduct } from "./fields/ChoiceProduct"
import {
  FormValues,
  IPersonalForm,
  THANKS_PAGE,
  Response,
  OfficeDataForSelect,
  OfficeDataWithMoreInfo,
  IDataCity,
} from "../types"
import { handleClickForm } from "../../../helpers/WatcherDL/utils/handleClickForm"
import ProgressBarGreen from "../../ProgressBarGreen"
import { calculateResultPercentageGreenBar, itemsSelect } from "../helper"
import fetchOffices from "../../PickupOfficeForForm/helpers/fetchOffices"
import { EsiaBtn } from "./EsiaBtn"
import { FioFieldRedesign } from "./fields/FioFieldRedesign"
import { PhoneFieldRedesign } from "./fields/PhoneFieldRedesign"
import { AcceptmentFieldRedesign } from "./fields/AcceptmentFieldRedesign"
import { TDLFormTypeInput } from "../../../helpers/WatcherDL/DLEvents/DLGAFormEvent"
import { pushToDataLayerOnce } from "../../../helpers/pushToDataLayerOnce"
import { ESIALogin } from "../../ESIALoginRedesign"
import { sendPostback } from "../../../helpers/postback/postback"
import { ChoiceOfTime } from "./fields/ChoiceOfTime"
import { formatCallTimeIso } from "./helpers/formatCallTimeIso"
import { DEFAULT_VALUE_SELECT_CHOISE_TIME } from "./helpers/variables"
import { PhoneFieldTwoMask } from "./fields/PhoneFieldTwoMask"

const DEFAULT_CITY_MOSCOW = {
  city_kladr_id: "7700000000000",
  kladr_id: "7700000000000",
  city: "Москва",
  city_with_type: "г Москва",
  geo_lat: "55.75396",
  geo_lon: "37.620393",
}

export const PersonalForm = ({
  frame = false,
  dataLayerName = "shortPersonalForm",
  hasBirth = false,
  hasCity = false,
  hasCityWithOffices = false,
  onSubmit,
  formBtnText = "Получить карту",
  noVerify = false,
  longForm = false,
  productName = "КК Халва",
  inputsColor = "white",
  fullWidth,
  variant = "standart",
  isGreenApprove,
  fioPlaceHolder = "ФИО",
  choiceProduct,
  setProductName,
  hasSelect = false,
  thanksPage = THANKS_PAGE.SUCCESS,
  progressBar,
  selectItems = itemsSelect,
  selectTitle = "Как вы узнали про акцию?",
  plaitMode,
  esiaBtn,
  cityPlaceholder = "Город",
  ESIALoginTitle,
  ESIALoginDescription,
  showESIA,
  btnCustomColor,
  logoSovcombank,
  successWithShop,
  choiceOfTime,
}: IPersonalForm) => {
  const { loanAmount, term } = useContext(CalcWithFormContext)
  const [showCustomInput, setShowCustomInput] = useState(false)
  const [focusPhone, setFocusPhone] = useState(false)
  const [officesFromRequest, setOfficesFromRequest] = useState<OfficeDataWithMoreInfo[]>([])
  const [officesForSelect, setOfficesForSelect] = useState<OfficeDataForSelect[]>([])
  const [cityForMap, setCityForMap] = useState<IDataCity>(DEFAULT_CITY_MOSCOW)
  const [officeFullData, setOfficeFullData] = useState<OfficeDataWithMoreInfo | null>(null)
  const previousValues = useRef<Record<string, string>>({})
  const defaultSubmit = (valuesToRequest: Record<string, any>) => {
    sendRequest(`/v2/app/public/start`, { ...valuesToRequest, phoneValidate: !noVerify })
      .then(({ status, requestId, partnerExternalId }: Response) => {
        sendPostback({ status, requestId, partnerExternalId }, productName)
        if (["pending", "repeated", "reject", "waitingDecision", "approve"].includes(status)) {
          setIDBValue("requestId", requestId)
          return Promise.resolve(status)
        }

        return Promise.reject(new Error(status))
      })
      .then((status: string) => {
        switch (status) {
          case "reject":
            DLFormSendEvent({
              result: "error",
              placement: "main",
              reason: "reject",
              eventAction: productName,
            })
            navigate(plaitMode ? "/plait/error-reject/" : "/error-reject/", {
              state: { verifyLogoSovcombank: logoSovcombank },
            })
            return
          case "repeated":
            DLFormSendEvent({
              result: "error",
              placement: "main",
              reason: "repeated",
              eventAction: productName,
            })
            navigate(plaitMode ? "/plait/error-repeat/" : "/error-repeat/", {
              state: { verifyLogoSovcombank: logoSovcombank },
            })
            return
          case "error":
            throw Error(status)
          case "approve":
            DLFormSendEvent({
              result: "success",
              placement: "main",
              eventLabel: globalThis?.location?.href,
              eventAction: productName,
            })
            break
          default:
            if (frame) {
              const formSentMessage = JSON.stringify({ action: "FORM_SENT" })
              window.parent.postMessage(formSentMessage, "*")
            }
            break
        }
        if (noVerify) {
          navigate(plaitMode ? "/plait/success/" : "/success/")
        } else {
          navigate("/verify/", {
            state: {
              haveCourier: longForm,
              thanksPage,
              plaitMode,
              verifyLogoSovcombank: logoSovcombank,
              successWithShop,
            },
          })
        }
      })
      .catch((e: Error) => {
        pushToDataLayer(events.applicationSend.error)
        pushToDataLayer(events.GAFormEvent.error)
        console.error(e, "error is sendRequest")

        navigate(plaitMode ? "/plait/error-technical/" : "/error-technical/", {
          state: {
            verifyLogoSovcombank: logoSovcombank,
            from: globalThis?.location?.href,
            errorInfo: `defaultSubmit, PersonalForm, formPKW, error: ${
              e?.message || "Unknown error"
            }`,
          },
        })
      })
      .finally(() => {
        DLGAFormEvent({
          eventCategory: "stepSuccess",
          eventAction: "shortPersonalForm",
          placement: "main",
        })
      })
  }

  const formik = useFormik<FormValues>({
    initialValues: {
      phone: "",
      fio: { data: { surname: null, name: null, patronymic: null } },
      agree: true,
      ...(hasBirth && { birthDate: "" }),
      ...(hasCity && {
        localityAddress: {
          value: "",
          data: null,
          unrestricted_value: "",
        },
      }),
      ...(hasCityWithOffices && {
        localityAddress: {
          value: "",
          data: null,
          unrestricted_value: "",
        },
        deliveryOffice: "",
      }),
      ...(hasSelect && {
        inviteFriendFromWhom: "",
        customTextField: "",
      }),
      callTime: DEFAULT_VALUE_SELECT_CHOISE_TIME,
    },
    validationSchema: FormSchema(hasBirth, hasSelect, hasCityWithOffices, selectItems, hasCity),
    validateOnChange: true,
    onSubmit: (values, { setSubmitting }) => {
      const formattedCallTime = formatCallTimeIso(values.callTime)

      const valuesToRequest = {
        agree: values.agree,
        surname: values.fio.data.surname,
        name: values.fio.data.name,
        patronymic: values.fio.data.patronymic,
        phone: values.phone.replace(/[- )(+]/g, ""),
        productName,
        additionalData: getAdditionalData(),
        sum: loanAmount,
        term,
        ...(hasBirth && {
          birthDate: formatBirthDate(values.birthDate),
        }),
        ...(hasCity && {
          localityAddress: values.localityAddress,
        }),
        ...(choiceOfTime &&
          formattedCallTime && {
            callTime: formattedCallTime,
          }),
        ...(hasCityWithOffices && {
          localityAddress: values.localityAddress,
          deliveryOffice: officesFromRequest.find(
            (office) => office.address === values.deliveryOffice
          )?.id,
        }),
        ...(hasSelect && {
          inviteFriendFromWhom:
            values.inviteFriendFromWhom === "Другое"
              ? values.customTextField
              : values.inviteFriendFromWhom,
        }),
      }

      setSubmitting(true)

      setIDBValue("phone", valuesToRequest.phone)
      if (values.callTime && values.callTime !== DEFAULT_VALUE_SELECT_CHOISE_TIME) {
        pushToDataLayerOnce("submittedCallTime", {
          event: "submittedCallTime",
          eventCategory: "success",
          eventAction: formattedCallTime,
          eventLabel: globalThis?.location?.href,
          placement: "main",
        })
      }
      if (!onSubmit) {
        defaultSubmit(valuesToRequest)
      } else {
        onSubmit(valuesToRequest)
      }
    },
  })

  const handleBlurWithTracking = async (e: React.FocusEvent<any>, field: TDLFormTypeInput) => {
    formik.handleBlur(e)
    const currentValue = e.currentTarget.value
    const isValid = await FormSchema(
      hasBirth,
      hasSelect,
      hasCityWithOffices,
      selectItems,
      hasCity
    ).fields[field]?.isValid(formik.values[field])
    const prevValid = previousValues.current[field]

    if (isValid && currentValue && prevValid && prevValid !== currentValue) {
      // Случай 1: изменено валидное значение на другое
      DLGAFormEvent({
        eventCategory: "fieldChanged",
        eventAction: field,
        placement: "main",
      })
      previousValues.current[field] = currentValue
    } else if (!currentValue && prevValid) {
      // Случай 2: полностью удалено валидное значение
      DLGAFormEvent({
        eventCategory: "fieldCleared",
        eventAction: field,
        placement: "main",
      })
      previousValues.current[field] = ""
    } else if (currentValue && isValid) {
      // Случай 3: первое заполнение, либо ввод после очистки
      previousValues.current[field] = currentValue
    }
  }

  const handleChange = (e: React.ChangeEvent<any>, field: TDLFormTypeInput) => {
    pushToDataLayerOnce(field, {
      event: "GAFormEvent",
      eventCategory: "fieldStarted",
      eventAction: field,
      eventLabel: globalThis?.location?.href,
      placement: "main",
    })
    formik.handleChange(e)
  }

  useEffect(() => {
    if (formik.isValid && formik.dirty) {
      const readyToSubmit = (() =>
        Object.entries(formik.values).every(([key, value]) => {
          if (key !== "fio") {
            return !!value
          }
          if (value && typeof value === "object" && "data" in value) {
            return !!value.data?.surname && !!value.data?.name
          }
          return false
        }))()
      if (readyToSubmit) {
        DLGAFormEvent({
          eventCategory: "allFieldsFilled",
          eventAction: "formReadyToSubmit",
          placement: "main",
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.isValid, formik.dirty])

  useEffect(() => {
    // отправка ошибок в dataLayer при повторном нажатии button submit
    if (formik.isSubmitting) {
      if (formik.submitCount > 1) {
        Object.entries(formik.errors).forEach((fieldKey) => {
          const [fieldName, errorMessage] = fieldKey
          if (fieldName === "fio" && typeof errorMessage === "object" && errorMessage.data) {
            pushToDataLayerInvalidField(fieldName)
          } else {
            pushToDataLayerInvalidField(fieldName)
          }
        })
      }
      pushToDataLayerSendForm()
    }
  }, [formik.isSubmitting, formik.errors, formik.submitCount])

  useEffect(() => {
    if (dataLayerName) {
      pushToDataLayerLoadedStep(dataLayerName)
    }
  }, [dataLayerName])

  useEffect(() => {
    if (!hasCityWithOffices) return
    if (cityForMap?.city_kladr_id) {
      fetchOffices({
        kladrID: cityForMap.city_kladr_id,
        latitude: parseFloat(cityForMap.geo_lat),
        longitude: parseFloat(cityForMap.geo_lon),
        noFiltrationOffices: true,
      })
        .then((data) => {
          if (!data || !Array.isArray(data) || data.length < 1) return
          setOfficesFromRequest(data)
          if (
            formik.values.deliveryOffice &&
            !data.find((item) => item.address === formik.values.deliveryOffice)
          ) {
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            handleChangeSelectOffice("")
          }
          setOfficesForSelect(data?.map((item) => ({ value: item.address, label: item.address })))
        })
        .catch((e: Error) => {
          console.error(e.message)
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cityForMap?.city_kladr_id])

  useEffect(() => {
    if (!formik.values.localityAddress?.data?.city_kladr_id || !hasCityWithOffices) return
    if (formik.values.localityAddress?.data?.city_kladr_id !== cityForMap.city_kladr_id) {
      setCityForMap(formik.values.localityAddress.data)
    }
  }, [cityForMap.city_kladr_id, formik.values?.localityAddress?.data, hasCityWithOffices])

  useEffect(() => {
    if (!(officesFromRequest.length > 0)) return
    const officeFromSelect = officesFromRequest.find(
      (office) => office.address === formik.values.deliveryOffice
    )
    setOfficeFullData(officeFromSelect || null)
  }, [formik.values.deliveryOffice, officesFromRequest])

  const handleSelectChange = (event: { target: { value: string } }) => {
    formik.setFieldValue("inviteFriendFromWhom", event.target.value)
    setShowCustomInput(event.target.value === "Другое")
  }

  const handleOnPastePhoneField = (e: React.ClipboardEvent<Element>) => {
    let phone = e.clipboardData.getData("text").replace(/\D/g, "")
    if (!phone) return
    if (phone.length === 11) {
      phone = phone.slice(1)
    }
    formik.setFieldValue("phone", phone)
  }

  const handleChangeSelectOffice = (arg: string) => {
    formik.setFieldValue("deliveryOffice", arg, true)
    if (!formik.touched?.deliveryOffice) {
      formik.setFieldTouched("deliveryOffice", true)
    }
  }

  const resultPercentageGreenBar = calculateResultPercentageGreenBar(formik)

  const handleESIAClickWithValidation = () => {
    formik.setTouched(
      {
        phone: true,
        fio: {
          data: {
            surname: true,
            name: true,
            patronymic: true,
          },
        },
        agree: true,
      },
      false
    )
  }

  return (
    <>
      {showESIA && (
        <ESIALogin
          productName={productName}
          title={ESIALoginTitle}
          description={ESIALoginDescription}
          formik={formik}
          errorFields={formik.errors.fio || formik.errors.phone || formik.errors.agree || null}
          onValidateRequest={handleESIAClickWithValidation}
          choiceOfTime={choiceOfTime}
        />
      )}
      <div>
        <form className={clsx(styles.form, styles[variant])}>
          {progressBar && (
            <ProgressBarGreen
              steps={1}
              widthAnimationBar={50}
              widthGreenBar={resultPercentageGreenBar}
              variant={variant}
            />
          )}
          {/* TODO: вынести fields отдельно. Для каждого fields в идеале сделать обертку. FormikForm должен быть оберткой, куда спускаются fields в качестве children */}
          <div
            onClick={() => handleClickForm}
            onKeyDown={() => handleClickForm}
            role="button"
            tabIndex={0}
          >
            {variant === "redesignPkw" ? (
              <>
                <FioFieldRedesign
                  handleBlur={(e) => handleBlurWithTracking(e, "fio")}
                  setFieldValue={formik.setFieldValue}
                  fioValue={formik.values.fio}
                  errors={formik.errors.fio}
                  touched={formik.touched.fio}
                  name="fio"
                  id="fio"
                  type="fio"
                  inputsColor={inputsColor}
                  isGreenApprove={isGreenApprove}
                  fioPlaceHolder={fioPlaceHolder}
                  setFocusPhone={setFocusPhone}
                  variant={variant}
                />
                <PhoneFieldRedesign
                  focus={focusPhone}
                  setFocus={setFocusPhone}
                  onChange={(e) => handleChange(e, "phone")}
                  handleBlur={(e) => handleBlurWithTracking(e, "phone")}
                  value={formik.values.phone}
                  error={formik.errors.phone}
                  touched={formik.touched.phone}
                  isFioFieldValidate
                  name="phone"
                  id="phone"
                  inputsColor={inputsColor}
                  isGreenApprove={isGreenApprove}
                  onPaste={(e) => handleOnPastePhoneField(e)}
                  variant={variant}
                />
              </>
            ) : (
              <>
                <FioField
                  handleBlur={(e) => handleBlurWithTracking(e, "fio")}
                  setFieldValue={formik.setFieldValue}
                  fioValue={formik.values.fio}
                  errors={formik.errors.fio}
                  touched={formik.touched.fio}
                  name="fio"
                  id="fio"
                  type="fio"
                  inputsColor={inputsColor}
                  isGreenApprove={isGreenApprove}
                  fioPlaceHolder={fioPlaceHolder}
                  setFocusPhone={setFocusPhone}
                  variant={variant}
                />
                <PhoneFieldTwoMask
                  focus={focusPhone}
                  setFocus={setFocusPhone}
                  onChange={(e) => handleChange(e, "phone")}
                  handleBlur={(e) => handleBlurWithTracking(e, "phone")}
                  value={formik.values.phone}
                  error={formik.errors.phone}
                  touched={formik.touched.phone}
                  isFioFieldValidate={Boolean(!formik.errors.fio)}
                  name="phone"
                  id="phone"
                  inputsColor={inputsColor}
                  isGreenApprove={isGreenApprove}
                  onPaste={(e) => handleOnPastePhoneField(e)}
                  variant={variant}
                />
              </>
            )}
            {choiceProduct && (
              <ChoiceProduct productName={productName} setProductName={setProductName} />
            )}
          </div>
          {hasBirth && (
            <BirthField
              onChange={(e) => handleChange(e, "birthDate")}
              handleBlur={(e) => handleBlurWithTracking(e, "birthDate")}
              value={formik.values.birthDate}
              error={formik.errors.birthDate}
              touched={formik.touched.birthDate}
              name="birthDate"
              id="birthDate"
              inputsColor={inputsColor}
            />
          )}
          {hasCity && formik.values.localityAddress && (
            <CityField
              setFieldValue={formik.setFieldValue}
              validateField={formik.validateField}
              touchField={formik.setFieldTouched}
              fieldValue={formik.values.localityAddress}
              error={formik.errors.localityAddress}
              touched={formik.touched.localityAddress}
              name="localityAddress"
              id="localityAddress"
              inputsColor={inputsColor}
              cityPlaceholder={cityPlaceholder}
            />
          )}
          {hasCityWithOffices && formik.values.localityAddress && (
            <>
              <CityField
                setFieldValue={formik.setFieldValue}
                validateField={formik.validateField}
                touchField={formik.setFieldTouched}
                fieldValue={formik.values.localityAddress}
                error={formik.errors.localityAddress}
                touched={formik.touched.localityAddress}
                name="localityAddress"
                id="localityAddress"
                inputsColor={inputsColor}
              />
              <div className={clsx(styles.selectField, styles.selectFieldOffice)}>
                <Select
                  fullWidth
                  name="deliveryOffice"
                  label="Адрес выбранного офиса"
                  inputsColor="white"
                  items={officesForSelect}
                  value={formik.values.deliveryOffice || ""}
                  onChange={(event: { target: { value: string } }) => {
                    handleChangeSelectOffice(event.target.value)
                  }}
                  error={formik.touched.deliveryOffice && formik.errors.deliveryOffice}
                  MenuProps={{
                    MenuListProps: {
                      className: styles.listProps,
                    },
                    classes: {
                      paper: styles.listPaper,
                    },
                  }}
                />
                {formik.errors.deliveryOffice && formik.touched.deliveryOffice && (
                  <FormHelperText error className={styles.acceptmentError}>
                    {formik.errors.deliveryOffice}
                  </FormHelperText>
                )}
              </div>
              <div className={styles.pickupContainer}>
                <PickupForForm
                  officesFromRequest={officesFromRequest}
                  initialScale={10}
                  kladr={cityForMap.city_kladr_id}
                  defLat={cityForMap.geo_lat}
                  defLon={cityForMap.geo_lon}
                  officeFullData={officeFullData}
                  changeOfficeAtSelect={handleChangeSelectOffice}
                />
              </div>
            </>
          )}
          {hasSelect && (
            <div className={styles.selectField}>
              <Select
                fullWidth
                label={selectTitle}
                inputsColor="white"
                items={selectItems}
                value={formik.values.inviteFriendFromWhom}
                onChange={handleSelectChange}
                onBlur={formik.handleBlur}
                name="extraSelectName"
                error={formik.errors.agree}
              />
              {showCustomInput && (
                <div className={styles.selectFieldOther}>
                  <MUITexteField
                    fullWidth
                    label="Введите свой вариант ответа"
                    value={formik.values.customTextField}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    name="customTextField"
                  />
                  <FormHelperText error className={styles.customTextFieldError}>
                    {formik.errors.customTextField && formik.touched.customTextField
                      ? formik.errors.customTextField
                      : ""}
                  </FormHelperText>
                </div>
              )}
              {formik.submitCount > 0 && formik.errors.inviteFriendFromWhom && (
                <FormHelperText error className={styles.acceptmentError}>
                  {formik.errors.inviteFriendFromWhom}
                </FormHelperText>
              )}
            </div>
          )}
          {choiceOfTime && (
            <ChoiceOfTime
              onBlur={formik.handleBlur}
              formik={formik}
              value={formik.values.callTime}
              name="callTime"
              title="Выберите удобное время для звонка"
            />
          )}
          {variant === "redesignPkw" ? (
            <AcceptmentFieldRedesign
              value={formik.values.agree}
              onChange={(e) => {
                formik.handleChange(e)
                DLClickEventExtended({
                  name: "aggrement",
                  placement: "main",
                  eventAction: "Чекбокс согласие",
                })
              }}
              error={formik.errors.agree}
              name="agree"
              id="agree"
              touched={formik.touched.agree}
              variant={variant}
            />
          ) : (
            <AcceptmentField
              value={formik.values.agree}
              onChange={(e) => {
                formik.handleChange(e)
                DLClickEventExtended({
                  name: "aggrement",
                  placement: "main",
                  eventAction: "Чекбокс согласие",
                })
              }}
              error={formik.errors.agree}
              name="agree"
              id="agree"
              touched={formik.touched.agree}
              variant={variant}
            />
          )}
          <div className={styles.btnsBlock}>
            <Button
              classes={{
                root: clsx(
                  styles.btn,
                  { [styles.btnWidth]: !fullWidth },
                  { [styles.withEsiaBtn]: esiaBtn },
                  { [styles.btnCustomColor]: btnCustomColor },
                  styles[variant]
                ),
              }}
              type="button"
              onClick={formik.submitForm}
              disabled={formik.isSubmitting}
              fullWidth={fullWidth}
            >
              {variant === "redesignPkw" && formik.isSubmitting ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                formBtnText
              )}
            </Button>
            {esiaBtn && <EsiaBtn productName={productName} />}
          </div>
        </form>
      </div>
    </>
  )
}
